/**
 * The matter a person last had open, remembered for this browser tab.
 *
 * The Case and Workbench tabs are empty states unless you arrived from a
 * matter, so leaving a matter for the Queue and clicking Case again used to
 * mean "No matter selected" and a trip back through the Queue. `AppShell`
 * records the matter on every Case or Workbench visit and points those two
 * tabs back at it.
 *
 * `sessionStorage`, not `localStorage`: it dies with the tab, so a matter id
 * never outlives the sitting that opened it. Every access is wrapped because
 * storage can be missing or can throw (a private window, blocked site data,
 * a full quota) and a convenience must never take the shell down with it —
 * the fallback is "nothing remembered", which is exactly how the tabs behaved
 * before this existed.
 *
 * The record carries the person it belongs to. Sign-out clears it, but a
 * session can also end without the button (it expires, and somebody else signs
 * in on the same shared machine), so a record written for one person is never
 * read back for another.
 *
 * Nothing here checks that the remembered matter exists: the shell records the
 * path before any request has gone out. The screen that opens it does the
 * checking, and a 404 there calls {@link forgetLastMatter} (see
 * `useForgetMissingMatter`), so a wrong id costs one "Could not load this
 * matter" card and not every click on the tab after it.
 */

const STORAGE_KEY = 'tmverse.lastMatter'

export interface LastMatter {
  matterId: string
  /** The run that was open, or `null` when only the matter was named. */
  runId: string | null
}

interface StoredMatter extends LastMatter {
  owner: string
}

function readRecord(): StoredMatter | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { owner, matterId, runId } = parsed as Record<string, unknown>
    if (typeof owner !== 'string' || typeof matterId !== 'string' || !matterId) return null
    if (runId !== null && typeof runId !== 'string') return null
    return { owner, matterId, runId: runId || null }
  } catch {
    return null
  }
}

/** The matter `owner` last had open, or `null` when nothing is remembered for them. */
export function readLastMatter(owner: string | null): LastMatter | null {
  if (!owner) return null
  const record = readRecord()
  if (!record || record.owner !== owner) return null
  return { matterId: record.matterId, runId: record.runId }
}

/** Remember `matter` for `owner`, replacing whatever was remembered before. */
export function writeLastMatter(owner: string, matter: LastMatter): void {
  try {
    const record: StoredMatter = { owner, matterId: matter.matterId, runId: matter.runId }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Nothing remembered is a fine outcome; see the module docstring.
  }
}

/** Forget everything. Sign-out calls this. */
export function clearLastMatter(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to forget if storage is unavailable.
  }
}

/** Forget the remembered matter, but only when it is `matterId`. */
export function forgetLastMatter(matterId: string): void {
  const record = readRecord()
  if (record && record.matterId === matterId) clearLastMatter()
}

/** Forget a record that belongs to anyone other than `owner`. */
export function forgetLastMatterUnlessOwnedBy(owner: string | null): void {
  const record = readRecord()
  if (record && record.owner !== owner) clearLastMatter()
}
